import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  CheckIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface Student {
  sophomorixUnid: string;
  Vorname: string;
  Name: string;
  Klasse: string;
}

interface RecipientManagerProps {
  elternbriefId?: number;
  isOpen: boolean;
  onClose: () => void;
  onRecipientsAdded: (recipients?: any[]) => void;
  selectedRecipientUnids?: string[];
}

const RecipientManager: React.FC<RecipientManagerProps> = ({
  elternbriefId,
  isOpen,
  onClose,
  onRecipientsAdded,
  selectedRecipientUnids = [] as string[]
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState<string[]>([]);
  const [existingRecipients, setExistingRecipients] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadStudents();
      loadExistingRecipients();
    }
  }, [isOpen, elternbriefId]);

  useEffect(() => {
    filterStudents();
  }, [students, searchTerm, selectedClass]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/students');
      if (response.data.success) {
        const studentsData: Student[] = response.data.data;
        console.log('Loaded students:', studentsData.length);
        
        // Check for duplicate sophomorixUnids
        const unids = studentsData.map((s: Student) => s.sophomorixUnid);
        const uniqueUnids = [...new Set(unids)];
        if (unids.length !== uniqueUnids.length) {
          console.warn('Duplicate sophomorixUnids found:', unids.length, 'vs', uniqueUnids.length);
          console.log('Duplicate unids:', unids.filter((unid: string, index: number) => unids.indexOf(unid) !== index));
        }
        
        setStudents(studentsData);
        
        // Extract unique classes
        const uniqueClasses = [...new Set(studentsData.map((s: Student) => s.Klasse))].sort();
        setClasses(uniqueClasses);
      }
    } catch (error: any) {
      console.error('Error loading students:', error);
      toast.error('Fehler beim Laden der Schüler');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingRecipients = async () => {
    if (!elternbriefId) {
      setExistingRecipients(selectedRecipientUnids);
      return;
    }
    
    try {
      const response = await api.get(`/massenmail/${elternbriefId}/recipients`);
      if (response.data.success) {
        const recipientUnids = (response.data.data as Array<{ schueler_unid: string }>).map((r) => r.schueler_unid);
        setExistingRecipients((prev): string[] => {
          const merged = new Set<string>([...prev, ...recipientUnids, ...selectedRecipientUnids]);
          const mergedArray: string[] = [...merged];
          return mergedArray;
        });
      }
    } catch (error: any) {
      console.error('Error loading existing recipients:', error);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!elternbriefId) {
      setExistingRecipients(selectedRecipientUnids);
    } else if (selectedRecipientUnids.length > 0) {
      setExistingRecipients((prev): string[] => {
        const merged = new Set<string>([...prev, ...selectedRecipientUnids]);
        const mergedArray: string[] = [...merged];
        return mergedArray;
      });
    }
  }, [selectedRecipientUnids, elternbriefId, isOpen]);

  const filterStudents = () => {
    let filtered = students;

    // Filter by class
    if (selectedClass) {
      filtered = filtered.filter(student => student.Klasse === selectedClass);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.Vorname.toLowerCase().includes(term) ||
        student.Name.toLowerCase().includes(term) ||
        student.Klasse.toLowerCase().includes(term)
      );
    }

    // Remove duplicates based on sophomorixUnid (just in case)
    const uniqueFiltered = filtered.filter((student, index, self) => 
      index === self.findIndex(s => s.sophomorixUnid === student.sophomorixUnid)
    );

    setFilteredStudents(uniqueFiltered);
  };

  const handleStudentToggle = (unid: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(unid)) {
      newSelected.delete(unid);
    } else {
      newSelected.add(unid);
    }
    setSelectedStudents(newSelected);
  };

  const handleSelectClass = (className: string) => {
    if (selectedClass === className) {
      setSelectedClass('');
      setSelectedStudents(new Set());
    } else {
      setSelectedClass(className);
      const classStudents = students.filter(s => s.Klasse === className);
      const availableStudents = classStudents.filter(s => !existingRecipients.includes(s.sophomorixUnid));
      setSelectedStudents(new Set(availableStudents.map(s => s.sophomorixUnid)));
    }
  };

  const handleAddRecipients = async () => {
    if (selectedStudents.size === 0) {
      toast.error('Bitte wählen Sie mindestens einen Schüler aus');
      return;
    }

    try {
      setLoading(true);
      const studentData = Array.from(selectedStudents).map(unid => ({ unid }));
      
      // If no elternbriefId, we're in "new" mode - just pass back the selected students
      if (!elternbriefId) {
        const selectedStudentsList = students.filter(s => selectedStudents.has(s.sophomorixUnid));
        console.log('Passing back selected students:', selectedStudentsList);
        toast.success(`${selectedStudentsList.length} Empfänger ausgewählt`);
        onRecipientsAdded(selectedStudentsList);
        onClose();
        
        // Reset form AFTER closing
        setTimeout(() => {
          setSelectedStudents(new Set());
          setSelectedClass('');
          setSearchTerm('');
        }, 100);
        
        setLoading(false);
        return;
      }
      
      const response = await api.post(`/massenmail/${elternbriefId}/recipients`, {
        schueler: studentData
      });

      if (response.data.success) {
        const addedCount = response.data.data.added.length;
        const errorCount = response.data.data.errors.length;
        
        if (addedCount > 0) {
          toast.success(`${addedCount} Empfänger hinzugefügt`);
        }
        
        if (errorCount > 0) {
          toast.error(`${errorCount} Empfänger konnten nicht hinzugefügt werden`);
        }

        onRecipientsAdded();
        onClose();
        
        // Reset form
        setSelectedStudents(new Set());
        setSelectedClass('');
        setSearchTerm('');
      }
    } catch (error: any) {
      console.error('Error adding recipients:', error);
      toast.error('Fehler beim Hinzufügen der Empfänger');
    } finally {
      setLoading(false);
    }
  };

  const isStudentAlreadyRecipient = (unid: string) => {
    return existingRecipients.includes(unid);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Empfänger hinzufügen
                </h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Filters */}
            <div className="mb-6 space-y-4">
              {/* Class Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Klasse auswählen
                </label>
                <div className="flex flex-wrap gap-2">
                  {classes.map((className) => (
                    <button
                      key={className}
                      onClick={() => handleSelectClass(className)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        selectedClass === className
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {className}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schüler suchen
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nach Name oder Klasse suchen..."
                  />
                </div>
              </div>
            </div>

            {/* Student List */}
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Laden...</p>
                </div>
              ) : filteredStudents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Keine Schüler gefunden
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredStudents.map((student, index) => {
                    const isSelected = selectedStudents.has(student.sophomorixUnid);
                    const isExisting = isStudentAlreadyRecipient(student.sophomorixUnid);
                    
                    return (
                      <div
                        key={`student-${student.sophomorixUnid}-${index}`}
                        className={`p-4 flex items-center justify-between ${
                          isExisting ? 'bg-gray-50 opacity-75' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleStudentToggle(student.sophomorixUnid)}
                            disabled={isExisting}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {student.Vorname} {student.Name}
                            </p>
                            <p className="text-sm text-gray-500">
                              Klasse {student.Klasse}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {isExisting && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckIcon className="h-3 w-3 mr-1" />
                              Bereits hinzugefügt
                            </span>
                          )}
                          {isSelected && !isExisting && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <PlusIcon className="h-3 w-3 mr-1" />
                              Ausgewählt
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selection Summary */}
            {selectedStudents.size > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>{selectedStudents.size}</strong> Schüler ausgewählt
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              onClick={handleAddRecipients}
              disabled={loading || selectedStudents.size === 0}
              className="w-full inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Hinzufügen...' : `${selectedStudents.size} Empfänger hinzufügen`}
            </button>
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipientManager;
